// ════════════════════════════════════════════════════════════════════════════════
//  PmWorkOrders.js — PM → Fleet work order requests                       #PM_WO_REQUEST_V1 (2026-10-28)
//
//  Bill: "Pipe in a WORK ORDER request from PMs to Fleet — unit number, picture, description, location (GPS
//  should provide), urgency — PM to see tracking as to who is dispatched and status. Not sure where to have this
//  land without making dashboard messy."   ·   "Field should not touch WO but could trigger them through DVIRs,
//  but PMs should also have the ability to trigger their own."
//
//  WHERE IT LANDS   + Work Order button in the PM header (pop-up, no tile) · a 🔧 WO chip + per-unit badges inside
//                   My Equipment Assigned (tracking lives where the PM already looks at his iron — no new panel).
//  HOW IT FLOWS     A request is a normal row in public.fleet_work_orders: source 'PM Request', fork 'bob' — it opens
//                   on Bob, same as a DVIR defect. Bob forks it (shop pool / mechanic / vendor / field fix) exactly as
//                   any other WO; the PM's badge follows: With Bob → Assigned · Kenny → In progress → Closed · Kenny.
//  UNIT             Equipment Master only (no free-typed unit #s — same no-drift rule as POs).
//  LOCATION         The unit's own Geotab / OEM fix first (the PM may not be standing at it); phone GPS as fallback.
//                   No unit fix → Bob's queue already shows "GPS not reporting" with the Cam/GPS triage button.
//  URGENCY          DOWN = Emergency type + text to the Fleet alert number · THIS WEEK = due in 7 days · ROUTINE.
// ════════════════════════════════════════════════════════════════════════════════
import { useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { unitKey } from "./unitValid";
import { woMutate, woNewId, woNextNo, woLive, woFork, woUploadPhoto, WoPhotoStrip } from "./WorkOrders";
import { useUnitCtx } from "./ShopDash";
import { isKitWo, kitTone, kitWho, kitClock } from "./PmKits";   // #PM_KIT_FLOW_V1

export const PM_WO_SOURCE = 'PM Request';
export const PM_WO_URGENCY = [
  { k: 'down', label: 'DOWN', hint: "Unit can't work — texts Fleet now" },
  { k: 'week', label: 'THIS WEEK', hint: 'Fix within the week' },
  { k: 'routine', label: 'ROUTINE', hint: 'Next time it is convenient' },
];
const lc = (s) => String(s || '').trim().toLowerCase();
const titleName = (e) => String(e || '').split('@')[0].replace(/[._]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const pad2 = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const fmtWhen = (s) => { const d = new Date(s); if (isNaN(d)) return ''; let h = d.getHours(); const ap = h >= 12 ? 'p' : 'a'; h = h % 12 || 12; return `${d.getMonth() + 1}/${d.getDate()} ${h}:${pad2(d.getMinutes())}${ap}`; };
const mapUrl = (g) => (g && g.lat != null && g.lng != null) ? `https://www.google.com/maps?q=${Number(g.lat).toFixed(5)},${Number(g.lng).toFixed(5)}` : '';

// Who has it and where it stands — in plain words for the PM.
export const woPmStatus = (o) => {
  if (!o) return { k: 'void', label: '' };
  if (o.status === 'done') return { k: 'done', label: `Closed${o.doneByName || o.doneBy ? ` · ${o.doneByName || titleName(o.doneBy)}` : ''}` };
  if (o.status === 'void') return { k: 'void', label: 'Voided' };
  if (o.status === 'moved') return { k: 'done', label: `Moved to job ${o.moveJob || ''}`.trim() };
  // #PM_KIT_FLOW_V1 — a PM kit: who has it and where its 7-day clock is
  if (isKitWo(o)) {
    if (o.pmKit.stage === 'build') return { k: 'bob', label: 'PM kit · Jerry building it' };
    const t = kitTone(o);
    return { k: t === 'bad' ? 'late' : t === 'warn' ? 'warn' : 'field', label: `PM kit · ${kitWho(o)} · ${kitClock(o)}` };
  }
  const f = woFork(o);
  if (f === 'vendor') return { k: 'vendor', label: `At vendor · ${o.shop || '3rd party'}` };
  if (f === 'field') return { k: 'field', label: `Field fix${o.fieldSupName ? ` · ${o.fieldSupName}` : ''}` };
  if (f === 'shop' && o.assignee) return { k: o.status === 'working' ? 'working' : 'assigned', label: `${o.status === 'working' ? 'In progress' : 'Assigned'} · ${o.assigneeName || titleName(o.assignee)}` };
  if (f === 'shop') return { k: 'pool', label: 'Shop pool · waiting on a mechanic' };
  return { k: 'bob', label: 'With Bob · not dispatched yet' };
};
const toneOf = (D, k) => ({ done: D.green, void: D.muted, vendor: '#A78BFA', field: D.gold, working: D.cyan, assigned: D.cyan, pool: D.blue, bob: D.orange, warn: '#F59E0B', late: D.red || '#EF4444' })[k] || D.muted;
const urgOf = (o) => o.urgency || (o.type === 'Emergency' ? 'down' : 'routine');
const urgTone = (D, k) => k === 'down' ? D.red : k === 'week' ? D.orange : D.muted;

// This PM's own requests (by PM code, or anything he opened himself).
export const pmWoMine = (orders, pmCode, upn) => (orders || []).filter(o => o && o.source === PM_WO_SOURCE && ((pmCode && o.pmCode === pmCode) || (upn && lc(o.openedBy) === lc(upn))));

export function PmWoBadge({ o, D, onClick }) {
  const st = woPmStatus(o); const c = toneOf(D, st.k);
  return (
    <span onClick={onClick} title={`${o.no} — ${st.label}`}
      style={{ cursor: onClick ? 'pointer' : 'default', fontSize: 10, fontWeight: 800, color: c, border: `1px solid ${c}88`, borderRadius: 8, padding: '1px 6px',
        whiteSpace: 'nowrap', flexShrink: 0, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>🔧 {st.label}</span>
  );
}

function Modal({ D, title, onClose, children, width }) {
  const body = (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,12,.74)', zIndex: 9000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '5vh 12px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: D.card, border: `1px solid ${D.line}`, borderRadius: 14, width: '100%', maxWidth: width || 540, padding: 16, color: D.text, boxShadow: '0 20px 60px -20px #000', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12, gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 900, letterSpacing: 1.5, color: D.gold, textTransform: 'uppercase' }}>{title}</div>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${D.line}`, color: D.muted, borderRadius: 8, padding: '2px 10px', cursor: 'pointer', fontSize: 14 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(body, document.body) : body;
}

// ── the request form ───────────────────────────────────────────────────────────
export function PmWoRequest({ D, sg, units, mineUnits, seedUnit, pmCode, pmName, upn, orders, onClose, onSaved }) {
  const unitCtx = useUnitCtx(sg);
  const [q, setQ] = useState(seedUnit && seedUnit.unit ? String(seedUnit.unit) : '');
  const [unit, setUnit] = useState(seedUnit && seedUnit.unit ? seedUnit : null);
  const [desc, setDesc] = useState('');
  const [urg, setUrg] = useState('week');
  const [photos, setPhotos] = useState([]);
  const [upBusy, setUpBusy] = useState(0);
  const [phoneLoc, setPhoneLoc] = useState(null);
  const [locMsg, setLocMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const mineSet = useMemo(() => new Set((mineUnits || []).map(u => unitKey(u))), [mineUnits]);
  const hits = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s || (unit && String(unit.unit) === q)) return [];
    const mine = (r) => (mineSet.has(unitKey(r.unit)) ? 1 : 0);
    return (units || []).filter(r => r && r.unit && (String(r.unit).toLowerCase().includes(s) || String(r.desc || '').toLowerCase().includes(s)))
      .sort((a, b) => mine(b) - mine(a) || String(a.unit).localeCompare(String(b.unit))).slice(0, 8);
  }, [q, unit, units, mineSet]);

  const ctx = unit ? unitCtx(unit) : null;
  const inv = ctx && ctx.inv;
  const openOnUnit = unit ? (orders || []).filter(o => woLive(o) && o.uk === unitKey(unit.unit)) : [];
  const gps = (ctx && ctx.gpsOk && ctx.stamp) ? { ...ctx.stamp, src: 'geotab' } : (phoneLoc || null);

  const lbl = { fontSize: 10, fontWeight: 800, letterSpacing: 1, color: D.muted, textTransform: 'uppercase', margin: '12px 0 5px' };
  const inp = { width: '100%', boxSizing: 'border-box', background: D.cardHi, border: `1px solid ${D.line}`, color: D.text, borderRadius: 8, padding: '9px 10px', fontSize: 14, outline: 'none' };
  const btn = { background: D.cardHi, border: `1px solid ${D.line}`, color: D.text, borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' };

  const pick = (r) => { setUnit(r); setQ(String(r.unit)); setPhoneLoc(null); setLocMsg(''); };
  const addFiles = async (fl) => {
    const files = Array.from(fl || []).slice(0, Math.max(0, 6 - photos.length));
    if (!files.length) return;
    setErr(''); setUpBusy(n => n + files.length);
    for (const f of files) {
      try { const meta = await woUploadPhoto(f); setPhotos(p => [...p, meta]); }
      catch (e) { setErr((e && e.message) || 'Photo upload failed.'); }
      finally { setUpBusy(n => n - 1); }
    }
  };
  const grabPhoneLoc = () => {
    if (!navigator.geolocation) { setLocMsg('This device has no location.'); return; }
    setLocMsg('Getting your location…');
    navigator.geolocation.getCurrentPosition(
      p => { const now = new Date().toISOString(); setPhoneLoc({ lat: p.coords.latitude, lng: p.coords.longitude, place: 'PM phone location', at: now, stampedAt: now, src: 'phone', acc: Math.round(p.coords.accuracy || 0) }); setLocMsg(''); },
      e => setLocMsg(e && e.code === 1 ? 'Location is blocked — allow it in the browser to use this.' : 'Could not get a location.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 });
  };

  const submit = async () => {
    if (!unit) { setErr('Pick the unit from the Equipment Master list.'); return; }
    if (!desc.trim()) { setErr('Describe the problem.'); return; }
    if (upBusy) { setErr('Wait for the photos to finish uploading.'); return; }
    setBusy(true); setErr('');
    try {
      const now = new Date().toISOString();
      const U = PM_WO_URGENCY.find(u => u.k === urg) || PM_WO_URGENCY[1];
      const due = urg === 'down' ? isoDay(new Date()) : urg === 'week' ? isoDay(new Date(Date.now() + 7 * 864e5)) : '';
      const who = pmName || titleName(upn);
      let made = null;
      await woMutate(list => {
        const o = {
          id: woNewId(), no: woNextNo(list), unit: unit.unit, uk: unitKey(unit.unit), desc: unit.desc || '', cat: unit.cat || '',
          fork: 'bob', source: PM_WO_SOURCE, type: urg === 'down' ? 'Emergency' : 'Unscheduled', urgency: urg, due,
          complaint: `[${U.label}] ${desc.trim()}`, status: 'open', assignee: '', shop: '', thirdParty: false,
          pmCode: pmCode || '', pmName: who, pmJob: (inv && inv.job) || '', pmSup: (inv && inv.who) || '',
          photos, ...(gps ? { gps } : {}),
          openedBy: lc(upn), openedByName: who, openedAt: now,
          log: [{ at: now, by: lc(upn), byName: who, text: `PM request (${U.label}) from ${who}${gps ? ` — 📍 ${gps.place || 'GPS'}` : ' — no GPS'}${photos.length ? ` — ${photos.length} photo${photos.length === 1 ? '' : 's'}` : ''}` }],
        };
        made = o;
        return [...list, o];
      });
      if (urg === 'down' && made) {
        const body = `DOPE WO DOWN: ${made.no} · unit ${made.unit}${made.desc ? ' ' + made.desc : ''} · ${who}: ${desc.trim().slice(0, 120)}${gps && gps.place ? ' · ' + gps.place : ''}`;
        fetch('/api/send-sms', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }) }).catch(() => {});
      }
      if (onSaved) onSaved(made);
    } catch (e) { setErr((e && e.message) || 'Could not save — try again.'); setBusy(false); }
  };

  return (
    <Modal D={D} title="🔧 Work order to Fleet" onClose={busy ? () => {} : onClose}>
      <div style={lbl}>Unit # (Equipment Master)</div>
      <input value={q} autoFocus={!unit} onChange={e => { setQ(e.target.value); if (unit && e.target.value !== String(unit.unit)) setUnit(null); }}
        placeholder="Type unit # or description…" style={inp} />
      {hits.length > 0 && (
        <div style={{ border: `1px solid ${D.line}`, borderRadius: 8, marginTop: 4, overflow: 'hidden' }}>
          {hits.map(r => (
            <div key={r.unit} onClick={() => pick(r)} style={{ padding: '7px 10px', cursor: 'pointer', fontSize: 13, borderBottom: `1px solid ${D.line}`, background: D.cardHi }}>
              {mineSet.has(unitKey(r.unit)) && <span style={{ color: D.gold }}>★ </span>}<b>{r.unit}</b> <span style={{ color: D.muted }}>{r.desc || ''}</span>
            </div>
          ))}
        </div>
      )}
      {q.trim() && !unit && !hits.length && <div style={{ fontSize: 11.5, color: D.orange, marginTop: 5 }}>Not on the Equipment Master — check the unit # (only Master units can get a work order).</div>}
      {unit && (
        <div style={{ marginTop: 8, padding: '8px 10px', background: D.bg, border: `1px solid ${D.line}`, borderRadius: 8, fontSize: 12, lineHeight: 1.6 }}>
          <div><b style={{ color: D.cyan }}>{unit.unit}</b> <span style={{ color: D.muted }}>{unit.desc || ''}</span></div>
          <div>📋 {inv ? <>{inv.job ? <b>{inv.job}</b> : <b>{inv.who || 'no job'}</b>}{inv.job && inv.who ? ` · ${inv.who}` : ''} <span style={{ color: D.muted }}>(Jerry's inventory {inv.day.slice(5).replace('-', '/')})</span></> : <span style={{ color: D.muted }}>not on this week's inventory</span>}</div>
          <div>{ctx && ctx.gpsOk
            ? <>📍 <b style={{ color: D.green }}>{ctx.place || 'GPS fix'}</b> <span style={{ color: D.muted }}>{ctx.when}</span>{ctx.map ? <> · <a href={ctx.map} target="_blank" rel="noreferrer" style={{ color: D.blue }}>map</a></> : null}</>
            : phoneLoc
              ? <>📍 <b style={{ color: D.green }}>Your phone location</b> <span style={{ color: D.muted }}>±{phoneLoc.acc || '?'} m</span> · <a href={mapUrl(phoneLoc)} target="_blank" rel="noreferrer" style={{ color: D.blue }}>map</a></>
              : <><b style={{ color: D.orange }}>⚠ Unit GPS not reporting{ctx && ctx.fix ? ` — last ${ctx.when}` : ''}</b>{' '}
                  <button type="button" onClick={grabPhoneLoc} style={{ ...btn, padding: '2px 8px', fontSize: 11 }}>📍 Use my location</button></>}
          </div>
          {locMsg && <div style={{ color: D.muted }}>{locMsg}</div>}
          {openOnUnit.length > 0 && (
            <div style={{ color: D.orange, marginTop: 2 }}>Already open on this unit: {openOnUnit.map(o => `${o.no} (${woPmStatus(o).label})`).join(' · ')} — add yours only if it's a different problem.</div>
          )}
        </div>
      )}

      <div style={lbl}>What's wrong</div>
      <textarea rows={3} value={desc} onChange={e => setDesc(e.target.value)} placeholder="What's wrong, what the crew is seeing…" style={{ ...inp, resize: 'vertical', fontFamily: 'inherit' }} />

      <div style={lbl}>Urgency</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {PM_WO_URGENCY.map(u => { const on = urg === u.k, c = urgTone(D, u.k); return (
          <button key={u.k} type="button" onClick={() => setUrg(u.k)} title={u.hint}
            style={{ flex: '1 1 120px', background: on ? `${c}22` : D.cardHi, border: `1px solid ${on ? c : D.line}`, color: on ? c : D.muted, borderRadius: 8, padding: '8px 10px', cursor: 'pointer', textAlign: 'left' }}>
            <div style={{ fontSize: 12.5, fontWeight: 900 }}>{u.label}</div><div style={{ fontSize: 10.5, fontWeight: 600, opacity: .85 }}>{u.hint}</div>
          </button>); })}
      </div>

      <div style={lbl}>Photos {photos.length ? `(${photos.length})` : ''}</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={btn}>📷 Take photo<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} /></label>
        <label style={btn}>🖼 Upload<input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); e.target.value = ''; }} /></label>
        {upBusy > 0 && <span style={{ fontSize: 11.5, color: D.muted }}>Uploading {upBusy}…</span>}
      </div>
      <WoPhotoStrip photos={photos} size={60} />

      {err && <div style={{ color: D.red, fontSize: 12, fontWeight: 700, marginTop: 10 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: D.muted }}>Goes to Bob's work order queue{urg === 'down' ? ' + texts the Fleet alert number' : ''}.</span>
        <div style={{ flex: 1 }} />
        <button type="button" onClick={onClose} disabled={busy} style={btn}>Cancel</button>
        <button type="button" onClick={submit} disabled={busy || upBusy > 0}
          style={{ ...btn, background: urg === 'down' ? D.red : D.blue, borderColor: urg === 'down' ? D.red : D.blue, color: '#0A0E1A', opacity: busy || upBusy ? .6 : 1 }}>
          {busy ? 'Sending…' : 'Send to Fleet'}</button>
      </div>
    </Modal>
  );
}

// ── tracking: the PM's requests + anything else open on his equipment ───────────────
function WoCard({ D, o }) {
  const st = woPmStatus(o); const c = toneOf(D, st.k); const u = urgOf(o);
  const log = (o.log || []).slice().reverse().slice(0, 6);
  return (
    <div style={{ background: D.cardHi, border: `1px solid ${D.line}`, borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '9px 10px', marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <b style={{ fontSize: 12 }}>{o.no}</b><b style={{ color: D.cyan }}>{o.unit}</b><span style={{ color: D.muted, fontSize: 11.5 }}>{o.desc}</span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
          {o.source === PM_WO_SOURCE && <span style={{ fontSize: 10, fontWeight: 900, color: urgTone(D, u) }}>{(PM_WO_URGENCY.find(x => x.k === u) || {}).label || ''}</span>}
          <span style={{ fontSize: 10.5, fontWeight: 800, color: c }}>{st.label}</span>
        </span>
      </div>
      <div style={{ fontSize: 12.5, margin: '5px 0', whiteSpace: 'pre-wrap' }}>{o.complaint}</div>
      <WoPhotoStrip photos={o.photos} size={48} />
      <div style={{ fontSize: 10.5, color: D.muted }}>
        {o.source} · {fmtWhen(o.openedAt)} · {o.openedByName || titleName(o.openedBy)}
        {o.gps ? <> · 📍 {o.gps.place || 'GPS'}{mapUrl(o.gps) ? <> (<a href={mapUrl(o.gps)} target="_blank" rel="noreferrer" style={{ color: D.blue }}>map</a>)</> : null}</> : null}
      </div>
      {log.length > 0 && (
        <div style={{ marginTop: 6, borderTop: `1px solid ${D.line}`, paddingTop: 5 }}>
          {log.map((l, i) => (
            <div key={i} style={{ fontSize: 10.5, color: i === 0 ? D.text : D.muted, lineHeight: 1.5 }}>
              <span style={{ color: D.muted }}>{fmtWhen(l.at)}</span> · {l.text}{l.byName ? <span style={{ color: D.muted }}> — {l.byName}</span> : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PmWoList({ D, mine, others, onClose, onNew }) {
  const [tab, setTab] = useState('open');
  const rank = (o) => ({ down: 0, week: 1, routine: 2 })[urgOf(o)];
  const open = (mine || []).filter(woLive).sort((a, b) => rank(a) - rank(b) || String(a.openedAt).localeCompare(String(b.openedAt)));
  const closed = (mine || []).filter(o => !woLive(o)).sort((a, b) => String(b.doneAt || b.updatedAt || '').localeCompare(String(a.doneAt || a.updatedAt || ''))).slice(0, 30);
  const other = (others || []).filter(woLive);
  const pill = (k, label) => (
    <button key={k} onClick={() => setTab(k)} style={{ background: tab === k ? D.gold : 'transparent', color: tab === k ? '#0A0E1A' : D.muted, border: `1px solid ${tab === k ? D.gold : D.line}`, borderRadius: 14, padding: '3px 11px', fontSize: 11.5, fontWeight: 800, cursor: 'pointer' }}>{label}</button>
  );
  const shown = tab === 'open' ? open : tab === 'closed' ? closed : other;
  return (
    <Modal D={D} title="🔧 My work orders" onClose={onClose} width={620}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        {pill('open', `Open · ${open.length}`)}{pill('closed', `Closed · ${closed.length}`)}{pill('units', `On my equipment · ${other.length}`)}
        <div style={{ flex: 1 }} />
        <button onClick={onNew} style={{ background: D.blue, color: '#0A0E1A', border: `1px solid ${D.blue}`, borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 900, cursor: 'pointer' }}>+ Work Order</button>
      </div>
      {tab === 'units' && <div style={{ fontSize: 11, color: D.muted, marginBottom: 8 }}>Other open work (DVIRs, shop, service) on units assigned to your jobs this week.</div>}
      {shown.length === 0
        ? <div style={{ color: D.muted, fontSize: 12.5, padding: '10px 2px' }}>{tab === 'open' ? 'No open requests. Send one with + Work Order.' : tab === 'closed' ? 'Nothing closed yet.' : 'No other open work on your equipment.'}</div>
        : <div style={{ maxHeight: '62vh', overflowY: 'auto', paddingRight: 2 }}>{shown.map(o => <WoCard key={o.id} D={D} o={o} />)}</div>}
    </Modal>
  );
}
